// ============================================================
// MusterBar - everyone you may act as, one click each, in the actor's
// column under its rail (2026-10-22, plan muster; memory muster-rulings).
//
// The GM running a table of NPCs paid a menu per switch (the strip's
// right-click, or the gallery). Here every person on the stage is a chip
// in initiative order, a control network folded under its controller,
// with NOBODY last. A click sets the same `actorId` every other door
// sets; nothing is gated (control networks grant no authority).
//
// The column ACTS AS; the strip above the map TARGETS. Left is me.
//
// The shape is the engine's (`engine/roster/muster`); this file draws it.
// Chips are memoised field by field (`sameChip`), so a wound tick on one
// combatant repaints one chip. Keyboard: one tab stop (the acting chip,
// else the first); the arrows walk the column, Enter or Space acts.
// ============================================================
import LinkedChevrons from './killTeam/LinkedChevrons';
import { memo, useCallback, type KeyboardEvent, type RefObject } from 'react';
import clsx from '../utils/clsx';
import { healthAmount, healthColor, healthFill } from '../engine/healthRamp';
import { actedSplit, sameChip, type Muster, type MusterBlock, type MusterChip } from '../engine/roster/muster';
import useStripOverflow from '../hooks/useStripOverflow';
import { useWoundDisplay } from '../hooks/useWoundDisplay';
import { liveryStyle } from '../utils/liveryStyle';
import { rovingIndex } from '../utils/rovingIndex';
import type { MusterStep } from '../utils/musterFit';
import styles from '../styles/MusterBar.module.css';

interface Props {
  muster: Muster;
  /** Act as this combatant, or nobody (null). */
  onSelect: (id: string | null) => void;
  /** How densely to draw (utils/musterFit): 1 full chips, 2 and on compact. */
  step?: MusterStep;
  /** The scrolling list, which the fit measures (hooks/useMusterFit). */
  listRef?: RefObject<HTMLDivElement | null>;
  /** At the fit's fourth step: whether the acted are shown (their stub opened), and the stub's click. */
  actedOpen?: boolean;
  onToggleActed?: () => void;
}

const SEAT = '[data-muster-seat]';

/** The wounds as this viewer may read them: numbers, or the band's name (the banded-wounds ruling). */
function woundsText(chip: MusterChip, showNumbers: boolean): string {
  const amount = healthAmount(chip.shown, chip.total, showNumbers);
  return chip.isHorde ? `magnitude ${amount}` : `wounds ${amount}`;
}

/** Where the chip stands in a network, for its tooltip. */
function networkText(chip: MusterChip): string | null {
  if (!chip.network) return null;
  if (chip.controls) return `controls ${chip.network}`;
  return chip.orphan ? `${chip.network}: no controller here` : `in ${chip.network}`;
}

/** The tooltip: who, how hurt, and every mark the chip wears. */
export function chipTitle(chip: MusterChip, showNumbers: boolean): string {
  const marks = [
    woundsText(chip, showNumbers),
    chip.turn && 'their turn',
    chip.acted && 'has acted',
    chip.owed && 'owes a reaction',
    chip.downed && 'down',
    chip.hidden && 'hidden from the players',
    networkText(chip),
  ].filter(Boolean);
  return `${chip.name} - ${marks.join(' - ')} - act as them`;
}

interface ChipProps {
  chip: MusterChip;
  focusable: boolean;
  showNumbers: boolean;
  /** One line from the fit's second step: the initiative on the right, the wounds as an underline. */
  compact: boolean;
  onSelect: (id: string) => void;
}

/** The initiative, with a gold ▸ on whose turn it is. */
function Init({ chip }: { chip: MusterChip }) {
  return (
    <span className={styles.init}>
      {chip.turn ? '▸' : ''}
      {chip.initiative ?? '-'}
    </span>
  );
}

/** A wounds fill on the health ramp: the full face's bar, or the compact face's underline. */
function Fill({ chip, className }: { chip: MusterChip; className: string }) {
  const width = `${healthFill(chip.shown, chip.total)}%`;
  return (
    <span className={className} aria-hidden="true">
      <i style={{ width, background: healthColor(chip.shown, chip.total) }} />
    </span>
  );
}

/** The second line: the initiative, then the wounds bar - or a horde's magnitude, which a bar would hide. */
function Vitals({ chip, showNumbers }: { chip: MusterChip; showNumbers: boolean }) {
  return (
    <span className={styles.line}>
      <Init chip={chip} />
      {chip.isHorde ? (
        <span className={styles.magnitude} style={{ color: healthColor(chip.shown, chip.total) }}>
          ⚔ {healthAmount(chip.shown, chip.total, showNumbers)}
        </span>
      ) : (
        <Fill chip={chip} className={styles.bar} />
      )}
    </span>
  );
}

/** The marks after a name: the controller's star, the orphan's amber question. */
function Marks({ chip }: { chip: MusterChip }) {
  return (
    <>
      {chip.controls && (
        <span className={styles.star} aria-hidden="true">
          ★
        </span>
      )}
      {chip.orphan && (
        <span className={styles.orphan} aria-hidden="true">
          ?
        </span>
      )}
      {chip.squad && <LinkedChevrons livery={chip.livery} size={10} />}
    </>
  );
}

/** The compact face: one line - a horde's ⚔, the name, the marks, the initiative - over a wounds underline. */
function CompactFace({ chip }: { chip: MusterChip }) {
  return (
    <>
      <span className={styles.line}>
        {chip.isHorde && <span aria-hidden="true">⚔</span>}
        <span className={styles.name}>{chip.name}</span>
        <Marks chip={chip} />
        <Init chip={chip} />
      </span>
      <Fill chip={chip} className={styles.underline} />
    </>
  );
}

/** The full face: the name and its marks, then the initiative and the wounds. */
function FullFace({ chip, showNumbers }: { chip: MusterChip; showNumbers: boolean }) {
  return (
    <>
      <span className={styles.line}>
        <span className={styles.name}>{chip.name}</span>
        <Marks chip={chip} />
      </span>
      <Vitals chip={chip} showNumbers={showNumbers} />
    </>
  );
}

function ChipButton({ chip, focusable, showNumbers, compact, onSelect }: ChipProps) {
  return (
    <button
      type="button"
      className={clsx(styles.chip, compact && styles.compact)}
      style={liveryStyle(chip.livery)}
      data-muster-seat=""
      data-testid={`muster-chip-${chip.id}`}
      data-combatant-id={chip.id}
      data-depth={chip.depth}
      data-acting={chip.acting || undefined}
      data-turn={chip.turn || undefined}
      data-acted={chip.acted || undefined}
      data-owed={chip.owed || undefined}
      data-hidden={chip.hidden || undefined}
      data-downed={chip.downed || undefined}
      data-controls={chip.controls || undefined}
      data-orphan={chip.orphan || undefined}
      data-sibling={chip.sibling || undefined}
      aria-pressed={chip.acting}
      tabIndex={focusable ? 0 : -1}
      title={chipTitle(chip, showNumbers)}
      onClick={() => onSelect(chip.id)}
    >
      {compact ? <CompactFace chip={chip} /> : <FullFace chip={chip} showNumbers={showNumbers} />}
    </button>
  );
}

/** The chip's props besides the chip itself, compared by identity. */
const CHIP_PROPS = ['focusable', 'showNumbers', 'compact', 'onSelect'] as const;

const Chip = memo(ChipButton, (a, b) => sameChip(a.chip, b.chip) && CHIP_PROPS.every((k) => a[k] === b[k]));

/** The keys a focused chip keeps: the window binds both to the attack, and claiming them cancelled the click. */
const ACTIVATE = new Set(['Enter', ' ']);

/** Arrow keys walk the seats; Enter and Space act on the focused one. Both stop here, short of the window's hotkeys. */
function onSeatKeys(e: KeyboardEvent<HTMLDivElement>) {
  if (ACTIVATE.has(e.key)) {
    e.stopPropagation();
    return;
  }
  const seats = Array.from(e.currentTarget.querySelectorAll<HTMLElement>(SEAT));
  const next = rovingIndex(e.key, seats.indexOf(document.activeElement as HTMLElement), seats.length);
  if (next === null) return;
  e.preventDefault();
  e.stopPropagation();
  seats[next].focus();
}

/** The one tab stop, among the chips SHOWN (a folded-away chip cannot hold it): the actor's, else the first. */
function focusIdOf(shown: readonly MusterChip[]): string | null {
  return (shown.find((c) => c.acting) ?? shown[0])?.id ?? null;
}

interface BlocksProps extends Omit<ChipProps, 'chip' | 'focusable'> {
  blocks: MusterBlock[];
  focusId: string | null;
}

/** One section's chips, each block's host first and its units after. */
function Blocks({ blocks, focusId, ...face }: BlocksProps) {
  return blocks.flatMap((b) => b.chips.map((c) => <Chip key={c.id} chip={c} focusable={c.id === focusId} {...face} />));
}

/** How the column scrolls (hooks/useStripOverflow): down, by its seats; the wheel stays the page's own. */
const COLUMN = { axis: 'y', seat: SEAT } as const;

/** One list element for both its owners: the fit measures it, the overflow scrolls it. */
function useListRef(listRef: Props['listRef'], scrollerRef: (el: HTMLElement | null) => void) {
  return useCallback(
    (el: HTMLDivElement | null) => {
      if (listRef) listRef.current = el;
      scrollerRef(el);
    },
    [listRef, scrollerRef],
  );
}

/** ▴ or ▾: one seat further, shown only while seats are hidden, disabled at its end. */
function MoreButton({ dir, disabled, onStep }: { dir: 1 | -1; disabled: boolean; onStep: (dir: 1 | -1) => void }) {
  const up = dir < 0;
  return (
    <button
      type="button"
      className={styles.more}
      data-testid={up ? 'muster-more-up' : 'muster-more-down'}
      aria-label={up ? 'Earlier in the muster' : 'Later in the muster'}
      disabled={disabled}
      onClick={() => onStep(dir)}
    >
      {up ? '▴' : '▾'}
    </button>
  );
}

/** The stub the acted fold into (the fit's fourth step): a count, and the click that shows them again. */
function ActedStub({ count, open, onToggle }: { count: number; open: boolean; onToggle?: () => void }) {
  return (
    <button
      type="button"
      className={styles.stub}
      data-testid="muster-acted-stub"
      aria-expanded={open}
      title={open ? 'Fold away those who have acted' : 'Show those who have acted'}
      onClick={onToggle}
    >
      {open ? '▾' : '▴'} {count} ACTED
    </button>
  );
}

/** What the column shows of the order: at the fourth step, the acted fold away unless their stub is open. */
function shownOrder(muster: Muster, step: MusterStep, actedOpen: boolean) {
  if (step < 4) return { blocks: muster.inOrder, stub: 0 };
  const { acted, rest } = actedSplit(muster.inOrder);
  const actedCount = acted.reduce((n, b) => n + b.chips.length, 0);
  return { blocks: actedCount > 0 && !actedOpen ? rest : muster.inOrder, stub: actedCount };
}

export default function MusterBar({ muster, onSelect, step = 1, listRef, actedOpen = false, onToggleActed }: Props) {
  const { showNumbers } = useWoundDisplay();
  const all = [...muster.inOrder, ...muster.notInOrder].flatMap((b) => b.chips);
  const actor = all.find((c) => c.acting)?.id ?? null;
  // The acting chip is kept clear of the fades; nothing is centred.
  const overflow = useStripOverflow(actor, null, COLUMN);
  const setList = useListRef(listRef, overflow.scrollerRef);
  const order = shownOrder(muster, step, actedOpen);
  const focusId = focusIdOf([...order.blocks, ...muster.notInOrder].flatMap((b) => b.chips));
  const face = { showNumbers, compact: step >= 2, onSelect };
  const { state } = overflow;
  return (
    <div
      className={styles.muster}
      role="toolbar"
      aria-orientation="vertical"
      aria-label="Act as"
      data-testid="muster"
      data-step={step}
      onKeyDown={onSeatKeys}
    >
      <span className={styles.caption}>MUSTER · {all.length}</span>
      {order.stub > 0 && <ActedStub count={order.stub} open={actedOpen} onToggle={onToggleActed} />}
      {state.overflow && <MoreButton dir={-1} disabled={state.atStart} onStep={overflow.step} />}
      <div
        className={styles.list}
        ref={setList}
        data-more-start={(state.overflow && !state.atStart) || undefined}
        data-more-end={(state.overflow && !state.atEnd) || undefined}
      >
        <Blocks blocks={order.blocks} focusId={focusId} {...face} />
        {muster.inOrder.length > 0 && muster.notInOrder.length > 0 && (
          <span className={styles.caption} data-testid="muster-caption-unordered">
            NOT IN ORDER
          </span>
        )}
        <Blocks blocks={muster.notInOrder} focusId={focusId} {...face} />
        <button
          type="button"
          className={styles.nobody}
          data-muster-seat=""
          data-testid="muster-nobody"
          aria-pressed={!actor}
          tabIndex={focusId ? -1 : 0}
          title="Act as nobody"
          onClick={() => onSelect(null)}
        >
          NOBODY
        </button>
      </div>
      {state.overflow && <MoreButton dir={1} disabled={state.atEnd} onStep={overflow.step} />}
    </div>
  );
}
